import cloneDeep from 'lodash-es/cloneDeep';
import set from 'lodash-es/set';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';

import { updateContext } from '@/modules/tryIt/redux/actions';
import { selectContext, selectIsReplay } from '@/modules/tryIt/redux/selectors';
import { capitalizeFirstLetter, removeEmptyObjects } from '@/util/util';

import { ContextVariable } from './ContextVariable';
import { useLockedContextVariables } from './useLockedContextVariables';
import { ContextSectionWrapper } from '../ContextSectionWrapper/ContextSectionWrapper';

type Props = { canEdit: boolean };

const EMPTY_VARIABLE = { isLocked: false, label: null, value: null };

const sortContextVariables = (a, b) => a.label.localeCompare(b.label);

export const VariablesSection = ({ canEdit }: Props) => {
  const storeContext = useSelector(selectContext);
  const isReplay = useSelector(selectIsReplay);
  const dispatch = useDispatch();
  const { t } = useTranslation();
  const { stateVariables, toggleLock } = useLockedContextVariables();

  const hideResetIcon = !canEdit || isReplay || stateVariables.length === 0;

  const resetUnlockedVariables = () => {
    const removed = stateVariables.filter((item) => !item.isLocked);

    const oldContext = cloneDeep(storeContext).slice(0, -1) ?? [];
    let newContext = cloneDeep(storeContext[storeContext.length - 1]) ?? {};

    removed.forEach((item) => {
      newContext = set(newContext, item.label, null);
    });

    dispatch(updateContext([...oldContext, removeEmptyObjects(newContext)]));
  };

  const hasEmptyVariables =
    stateVariables.length === 0 || stateVariables.some((variable) => variable.value === null);

  if ((!canEdit || isReplay) && hasEmptyVariables) {
    return null;
  }

  return (
    <ContextSectionWrapper
      title={capitalizeFirstLetter(t('common.context_variables'))}
      onClick={resetUnlockedVariables}
      hideIcon={hideResetIcon}
    >
      <div className="flex flex-col gap-1">
        {/* List of context variables */}
        {[...stateVariables].sort(sortContextVariables).map((variable) => (
          <ContextVariable
            canEdit={canEdit && !isReplay}
            key={variable.label}
            variable={variable}
            onButtonClick={() => toggleLock(variable)}
          />
        ))}

        {/* New context variable field */}
        {canEdit && !isReplay && (
          <ContextVariable canEdit={canEdit} variable={EMPTY_VARIABLE} isLast />
        )}
      </div>
    </ContextSectionWrapper>
  );
};
